import React, { useMemo, useState } from 'react';
import { Building2, ShieldCheck, X } from 'lucide-react';

import { Button } from '../../ComponnentsUI/common/Button';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Select } from '../../ComponnentsUI/common/Select';
import { Badge } from '../../ComponnentsUI/common/Badge';
import { InlineSpinner } from '../../ComponnentsUI/common/LoadingSpinner';
import { ConfirmDialog } from '../../ComponnentsUI/common/ConfirmDialog';
import { useRoles } from '../../../helpers/api/roles/roles';
import { OrgUnitNode, useOrgTree } from '../../../helpers/api/org-units/org-units';
import {
  useAssignRole,
  useEffectivePermissions,
  useRevokeRole,
  useUserAssignments,
} from '../../../helpers/api/role-assignments/role-assignments';

function flatten(node: OrgUnitNode | null | undefined): OrgUnitNode[] {
  if (!node) return [];
  return [node, ...(node.children ?? []).flatMap(flatten)];
}

/** id → "Region › Province" ancestor path, to disambiguate same-named units. */
function buildPathMap(root: OrgUnitNode | null | undefined): Record<string, string> {
  const paths: Record<string, string> = {};
  const walk = (n: OrgUnitNode, ancestors: string[]) => {
    paths[n.id] = ancestors.join(' › ');
    (n.children ?? []).forEach(c => walk(c, [...ancestors, n.name]));
  };
  if (root) walk(root, []);
  return paths;
}

// Display names for permission groups ("patient" in "patient.read").
const ENTITY_LABEL: Record<string, string> = {
  patient: 'Patient',
  study: 'Study',
  report: 'Report',
  user: 'User',
  org_unit: 'Org Unit',
  role: 'Role',
  pacs_device: 'PACS Device',
  audit_log: 'Audit Log',
  appointment: 'Appointment',
};

const humanize = (s: string) =>
  s.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

export default function UserRolesModal({
  uid,
  userName,
  onClose,
}: {
  uid: string;
  userName: string;
  onClose: () => void;
}) {
  const { data: roles } = useRoles();
  const { data: tree } = useOrgTree();
  const { data: assignments, isLoading, refetch } = useUserAssignments(uid);
  const { data: effective, refetch: refetchEff } = useEffectivePermissions(uid);

  const [roleId, setRoleId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<
    { role_id: string; org_unit_id: string } | null
  >(null);

  const nodes = useMemo(() => flatten(tree), [tree]);
  const nodePaths = useMemo(() => buildPathMap(tree), [tree]);
  const roleName = (id: string) => roles?.find(r => r.id === id)?.name ?? id.slice(0, 8);
  const nodeOf = (id: string) => nodes.find(x => x.id === id);
  const nodeName = (id: string) => nodeOf(id)?.name ?? id.slice(0, 8);

  const refresh = () => {
    refetch();
    refetchEff();
  };
  const assign = useAssignRole(uid, () => {
    setRoleId('');
    setNodeId('');
    refresh();
  });
  const revoke = useRevokeRole(uid);

  const roleOptions = (roles ?? []).map(r => ({ value: r.id, label: `${r.name} (${r.category.toLowerCase()})` }));
  // A role is only assignable at its own org level(s); restrict the node picker to match.
  const allowedLevels = roles?.find(r => r.id === roleId)?.assignable_levels ?? null;
  const nodeOptions = nodes
    .filter(n => !allowedLevels || allowedLevels.includes(n.type))
    .map(n => ({
      value: n.id,
      label: `${n.name}${n.type === 'FACILITY' ? ' — Facility' : ` — ${n.type.toLowerCase()}`}`,
    }));

  // Catch duplicates before the request: the backend would reject them anyway,
  // but an inline hint beats an error toast.
  const isDuplicate = Boolean(
    roleId &&
      nodeId &&
      assignments?.some(a => a.role_id === roleId && a.org_unit_id === nodeId)
  );

  // "patient.read" → { patient: ['read', ...], ... } for a grouped, readable preview.
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, string[]> = {};
    Object.keys(effective?.permissions ?? {})
      .sort()
      .forEach(p => {
        const [entity, ...rest] = p.split('.');
        (groups[entity] ||= []).push(rest.join('.') || p);
      });
    return groups;
  }, [effective]);

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <Modal.Header
        title={`Roles for ${userName}`}
        onClose={onClose}
        description={
          <>
            A role is granted <span className="font-medium">at a node</span>. A user can hold
            several (e.g. OB at one facility, consultant at another).
          </>
        }
      />
      <Modal.Body>
        <div className="mb-5">
          <div className="mb-2 text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
            Assigned roles
            {assignments?.length ? (
              <span className="ml-1.5 rounded-full bg-card-color-200 px-1.5 py-0.5 text-[10px] font-medium text-title-page-color-300 dark:bg-gray-700 dark:text-gray-400">
                {assignments.length}
              </span>
            ) : null}
          </div>
          {isLoading ? (
            <InlineSpinner className="py-4" />
          ) : !assignments?.length ? (
            <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-6 text-center dark:border-gray-700">
              <ShieldCheck size={22} className="text-title-page-color-300 dark:text-gray-500" />
              <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                No roles assigned yet. Use the form below to grant one.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {assignments.map(a => {
                const node = nodeOf(a.org_unit_id);
                return (
                  <div
                    key={`${a.role_id}-${a.org_unit_id}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 transition-colors hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                  >
                    <span className="flex min-w-0 items-center gap-2.5 text-sm text-title-page-color-100 dark:text-gray-200">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                        <ShieldCheck size={15} />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{roleName(a.role_id)}</span>
                        <span className="flex items-center gap-1.5 text-xs text-title-page-color-300 dark:text-gray-400">
                          <Building2 size={11} />
                          <span className="truncate">
                            {nodePaths[a.org_unit_id]
                              ? `${nodePaths[a.org_unit_id]} › ${nodeName(a.org_unit_id)}`
                              : nodeName(a.org_unit_id)}
                          </span>
                          {node && <Badge text={node.type.toLowerCase()} variant="neutral" />}
                        </span>
                      </span>
                    </span>
                    <button
                      onClick={() =>
                        setRevokeTarget({ role_id: a.role_id, org_unit_id: a.org_unit_id })
                      }
                      className="shrink-0 rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      title="Revoke this role"
                      aria-label={`Revoke ${roleName(a.role_id)} at ${nodeName(a.org_unit_id)}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mb-5 rounded-lg bg-card-color-200/50 dark:bg-gray-700/40 p-3">
          <div className="mb-2 text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
            Assign a role
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              className="flex-1"
              options={roleOptions}
              value={roleId}
              placeholder="Select role"
              onChange={e => {
                setRoleId(e.target.value);
                setNodeId('');
              }}
            />
            <Select
              className="flex-1"
              options={nodeOptions}
              value={nodeId}
              disabled={!roleId}
              placeholder={
                !roleId
                  ? 'Select a role first'
                  : allowedLevels && allowedLevels.length === 0
                  ? 'Not assignable to a node'
                  : 'Select node'
              }
              onChange={e => setNodeId(e.target.value)}
            />
            <Button
              onClick={() => assign.mutate({ role_id: roleId, org_unit_id: nodeId })}
              disabled={!roleId || !nodeId || isDuplicate || assign.isPending}
            >
              {assign.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </div>
          {!roleId && (
            <p className="mt-1.5 text-xs text-title-page-color-300 dark:text-gray-400">
              Pick a role first — the node list only shows levels where that role can be assigned.
            </p>
          )}
          {isDuplicate && (
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
              This user already holds {roleName(roleId)} at {nodeName(nodeId)}.
            </p>
          )}
        </div>

        {effective && (
          <div>
            <div className="mb-1 text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
              Effective permissions
            </div>
            <p className="mb-2 text-xs text-title-page-color-300 dark:text-gray-400">
              The combined result of all roles above — what this user can actually do.
            </p>
            {Object.keys(groupedPermissions).length === 0 ? (
              <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                None — this user has no permissions yet.
              </p>
            ) : (
              <div className="space-y-2">
                {Object.keys(groupedPermissions).map(entity => (
                  <div key={entity} className="flex flex-wrap items-baseline gap-1.5">
                    <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-title-page-color-300 dark:text-gray-500">
                      {ENTITY_LABEL[entity] ?? humanize(entity)}
                    </span>
                    {groupedPermissions[entity].map(action => (
                      <span
                        key={`${entity}.${action}`}
                        title={`${entity}.${action}`}
                        className="rounded-md bg-card-color-200 dark:bg-gray-700 px-2 py-0.5 text-xs text-title-page-color-300 dark:text-gray-300"
                      >
                        {humanize(action)}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>

      <ConfirmDialog
        isOpen={Boolean(revokeTarget)}
        title="Revoke this role?"
        message={
          revokeTarget ? (
            <>
              <span className="font-medium">{userName}</span> will lose{' '}
              <span className="font-medium">{roleName(revokeTarget.role_id)}</span> at{' '}
              <span className="font-medium">{nodeName(revokeTarget.org_unit_id)}</span> and any
              access it grants.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Revoke"
        isPending={revoke.isPending}
        onConfirm={() =>
          revokeTarget &&
          revoke.mutate(revokeTarget, {
            onSuccess: () => {
              setRevokeTarget(null);
              refresh();
            },
          })
        }
        onCancel={() => setRevokeTarget(null)}
      />
    </Modal>
  );
}
